/**
 * A **surface** — where a message or an impression actually happens.
 *
 * WHY `Channel` WAS NOT ENOUGH
 * -----------------------------------------------------------------------------
 * `CHANNEL_VALUES` is `['email', 'whatsapp']`, and it answers "which provider
 * carries this". That was the whole question while Wend sent two kinds of
 * message. It stops being the question the moment one channel carries two
 * products: a WhatsApp campaign is a message to a contact with a per-recipient
 * delivery result; a WhatsApp status is a post to an audience with no recipient
 * list and no delivery at all. Both are `whatsapp`. Neither can answer the
 * other's questions. *(Slice 46: the status a business can buy through Meta is
 * an ad in the Updates tab — Meta has no API call that posts a business's own
 * status — so `whatsapp-status` is a paid surface with no channel, measured as
 * the other ads are. The argument stands: one provider, and questions that do
 * not cross between its surfaces.)*
 *
 * A surface is `channel × placement`, and it is the unit every figure in this
 * product is attributed to from here on. `Channel` is not deleted and does not
 * change: it still names the provider, the credential and the credit ledger,
 * which are genuinely per-channel.
 *
 * WHY CAPABILITIES ARE DATA AND NOT A COMMENT
 * -----------------------------------------------------------------------------
 * This is the file that exists to stop this project's worst shipped defect
 * happening again at six times the surface area. That defect was a bounce rate
 * computed over every channel and drawn against AWS's email-only threshold: a
 * workspace sitting exactly on Amazon's 5% line rendered as 1.55% behind a
 * green badge.
 *
 * The repair then was a comment saying "email only". A comment cannot be asked
 * a question. {@link SURFACE_CAPABILITY} can: it says, per surface, which
 * measures that surface is *able* to report, and {@link surfaceReports} is the
 * question to ask before drawing one. A surface with no `delivered` cannot be
 * asked for a delivery rate, and a surface with no `spend` cannot be asked
 * what it cost — not by convention, but because the answer is `false` and
 * there is nothing to divide by.
 *
 * Slice 45's comparison is the first thing to ask it: spend is the first
 * measure drawn across surfaces, and `surfaceComparison` sets a surface's
 * spend only where this answers yes — so the message surfaces' "Not measured"
 * is this table's answer, not a comment's. The reach ledger gates on built
 * and `kind`, and slice 44's per-surface figures are each surface's own read,
 * in its own words, with no measure shared across surfaces to gate.
 *
 * WHY THERE IS NO `rate` ANYWHERE IN HERE
 * -----------------------------------------------------------------------------
 * Deliberate, and the most important line in the file. A rate needs a
 * denominator, and **no two surfaces share one**. Email's delivered, WhatsApp's
 * sent and an ad set's impressions are three different populations, and any
 * percentage computed across them is the original defect wearing a new name.
 *
 * The one thing that *is* comparable across surfaces is **people**, each
 * counted by its own surface: email and WhatsApp count this workspace's
 * contacts, by person (`person_id`, since slice 58), the unit this product
 * de-duplicates humans by; an ad surface counts the people Meta showed it to,
 * by Meta's count. Comparable as people, never one population — nothing adds
 * them up, and the reach ledger, which counts contacts, holds only the
 * surfaces that address them. It is why this file defines measures and
 * capabilities but never a ratio.
 *
 * WHY EVERY SURFACE THAT DOES NOT EXIST YET IS STILL LISTED
 * -----------------------------------------------------------------------------
 * `sms-campaign` has no adapter, no credential and no table. It is here
 * because the alternative is a union that widens every time a channel ships,
 * and a widening union is a change to every exhaustive switch in the product.
 * (`facebook-ads` and `instagram-ads` were in this sentence until slice 44
 * drew them, and `whatsapp-status` until slice 46.)
 *
 * **Listing a surface is not the same as drawing one.** {@link SURFACE_BUILT}
 * says which can answer today, and nothing renders a row, a tab or an empty
 * figure for one that cannot. A placeholder for an unbuilt channel is a promise
 * on a dashboard.
 */

import { z } from 'zod';

import { channelSchema, type Channel } from '../enums.js';
/* Type-only: erased at load, so no import cycle between the two contracts. */
import type { MetaPlacement } from './meta-ads-read.js';

/* -------------------------------------------------------------------------- */
/* The surfaces                                                               */
/* -------------------------------------------------------------------------- */

export const SURFACE_VALUES = [
  'email-campaign',
  'whatsapp-campaign',
  'whatsapp-status',
  'facebook-ads',
  'instagram-ads',
  'sms-campaign',
] as const;

export const surfaceSchema = z.enum(SURFACE_VALUES);
export type Surface = z.infer<typeof surfaceSchema>;

/**
 * How a surface reaches somebody, and it decides which questions are coherent.
 *
 * - `direct` — Wend holds the recipient list and every message has a result
 *   per person. Delivery, bounces and suppression all mean something.
 * - `broadcast` — posted to an audience the platform holds. Nobody is
 *   addressed, so there is no delivery, no bounce and no suppression; there
 *   are views. No surface is broadcast today: WhatsApp status was, until slice
 *   46 found that what Meta sells there is an ad.
 * - `paid` — an auction buys impressions from an audience the platform builds.
 *   No recipients, no delivery, and the defining measure is money.
 */
export const SURFACE_KIND_VALUES = ['direct', 'broadcast', 'paid'] as const;
export const surfaceKindSchema = z.enum(SURFACE_KIND_VALUES);
export type SurfaceKind = z.infer<typeof surfaceKindSchema>;

/**
 * Every measure any surface can report.
 *
 * One closed list rather than a union per surface, so a new surface cannot
 * invent a measure nobody has reconciled against the others. Adding one here
 * is a deliberate act with a compile error in every capability row.
 *
 * `reached` is the only member every surface can answer, and it is the ledger's
 * whole basis. The rest are surface-specific by construction.
 */
export const SURFACE_MEASURE_VALUES = [
  /** People this surface could put something in front of. Every surface has it. */
  'reached',
  /** Handed to the provider. `direct` only. */
  'accepted',
  /** Confirmed arrival. `direct` only, and only once the provider reports. */
  'delivered',
  /** Permanently undeliverable. `direct` only; email's bounce, WhatsApp's undeliverable. */
  'failed',
  /** Opened, viewed or seen, by whatever the surface's own word is. */
  'viewed',
  /** A link followed, or the ad clicked. */
  'clicked',
  /** Money. `paid` only. */
  'spend',
  /** Auction impressions. `paid` only, and NOT interchangeable with `reached`. */
  'impressions',
] as const;

export const surfaceMeasureSchema = z.enum(SURFACE_MEASURE_VALUES);
export type SurfaceMeasure = z.infer<typeof surfaceMeasureSchema>;

/**
 * Who carries it.
 *
 * **Not `Channel`.** `Channel` is `email | whatsapp` and names the thing a
 * message is, which cannot describe a Facebook ad — it is neither, and it is
 * carried by the same company as WhatsApp. Typing ads as `whatsapp` to make
 * them fit would be exactly the category error this file exists to prevent.
 *
 * `meta` covers WhatsApp messaging and the three ad surfaces — Facebook,
 * Instagram and WhatsApp status — because they share one Business Manager, one
 * app and one token family, which is what a provider means here.
 */
export const SURFACE_PROVIDER_VALUES = ['ses', 'meta', 'sms'] as const;
export const surfaceProviderSchema = z.enum(SURFACE_PROVIDER_VALUES);
export type SurfaceProvider = z.infer<typeof surfaceProviderSchema>;

export interface SurfaceCapability {
  readonly provider: SurfaceProvider;
  /** The message channel, where this surface is one. Null for ads. */
  readonly channel: Channel | null;
  readonly kind: SurfaceKind;
  /** What a person calls it. The only string a screen may print for a surface. */
  readonly label: string;
  /** Which measures this surface can report. Everything else is unanswerable. */
  readonly measures: readonly SurfaceMeasure[];
}

/**
 * Total over {@link SURFACE_VALUES}, so a new surface is a compile error here
 * rather than a row that silently reports nothing.
 */
export const SURFACE_CAPABILITY: Readonly<Record<Surface, SurfaceCapability>> = {
  'email-campaign': {
    provider: 'ses',
    channel: 'email',
    kind: 'direct',
    label: 'Email',
    measures: ['reached', 'accepted', 'delivered', 'failed', 'viewed', 'clicked'],
  },
  'whatsapp-campaign': {
    provider: 'meta',
    channel: 'whatsapp',
    kind: 'direct',
    /*
     * The key says `campaign` because WhatsApp carries two surfaces that are
     * not the same product: this one is addressed to contacts and has
     * delivery; the ad in Status (slice 46) is shown and does not. The label
     * stays "WhatsApp", what every campaign screen prints, because the other
     * surface's own label — "WhatsApp Status ads" — names the difference
     * wherever the two sit together.
     */
    label: 'WhatsApp',
    measures: ['reached', 'accepted', 'delivered', 'failed', 'viewed'],
  },
  'whatsapp-status': {
    provider: 'meta',
    /*
     * Slice 46: an ad, so no message channel. Meta offers no API call that
     * posts a business's own status; what a business can put in the Updates
     * tab through Meta is an ad in Status — the Marketing API's `whatsapp`
     * placement, position `status` — bought, measured and billed as the other
     * two ad surfaces are. This row said `channel: 'whatsapp'`, `kind:
     * 'broadcast'` and `['reached', 'viewed']` while it was a guess about an
     * organic post; it is now what Meta actually sells.
     */
    channel: null,
    kind: 'paid',
    label: 'WhatsApp Status ads',
    /*
     * Still no `accepted`, `delivered` or `failed`: an ad is shown, not
     * addressed, so there is no recipient row to succeed or fail.
     */
    measures: ['reached', 'impressions', 'clicked', 'spend'],
  },
  'facebook-ads': {
    provider: 'meta',
    /* An ad is not a message, so it has no channel. */
    channel: null,
    kind: 'paid',
    label: 'Facebook ads',
    measures: ['reached', 'impressions', 'clicked', 'spend'],
  },
  'instagram-ads': {
    provider: 'meta',
    channel: null,
    kind: 'paid',
    label: 'Instagram ads',
    measures: ['reached', 'impressions', 'clicked', 'spend'],
  },
  'sms-campaign': {
    provider: 'sms',
    channel: null,
    kind: 'direct',
    label: 'SMS',
    /*
     * Delivery and failure only. No opens, no clicks: a plain SMS carries no
     * pixel and no rewritten link, so a product that showed an SMS open rate
     * would be showing a number nobody measured.
     */
    measures: ['reached', 'accepted', 'delivered', 'failed'],
  },
};

/**
 * Which surfaces can answer today.
 *
 * Not a feature flag and not a setting — a statement about whether an adapter
 * exists. A surface outside this list has no code behind it, and nothing in the
 * product may draw a row, a tab, a zero or an empty figure for one.
 *
 * `facebook-ads` and `instagram-ads` are built: slice 41's read reports both
 * by placement, and slices 42 and 43 compose and run their campaigns. This
 * header promised them for slice 41, and the list was not changed then; slice
 * 44, which draws them outside Integrations for the first time, changed it.
 * `whatsapp-status` joined in slice 46 as the ad in Status: the same account
 * read reports it as the `whatsapp` placement. `sms-campaign` has no adapter
 * and no date.
 *
 * **Built is not "in every ledger".** An ad's reach is people Meta showed it
 * to, counted by Meta — not contacts in this workspace — so the Overview's
 * reach ledger, which counts contacts, gates on `kind === 'direct'` as well.
 */
export const SURFACE_BUILT: readonly Surface[] = [
  'email-campaign',
  'whatsapp-campaign',
  'facebook-ads',
  'instagram-ads',
  'whatsapp-status',
];

/** The order a reader meets them in: what is built, then what is not. */
export const SURFACE_ORDER: readonly Surface[] = [
  'email-campaign',
  'whatsapp-campaign',
  'facebook-ads',
  'instagram-ads',
  /* Slice 46: an ad surface now, so beside the other two. */
  'whatsapp-status',
  'sms-campaign',
];

/* -------------------------------------------------------------------------- */
/* The gate                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Whether this surface can report this measure at all.
 *
 * **The gate, and the reason this file exists.** Every caller that wants a
 * figure goes through it, so "WhatsApp status has no bounce rate" is a question
 * the code can answer rather than a fact a reader has to already know.
 *
 * It is a capability, never a measurement: `true` means the surface *could*
 * report this, not that it has. A surface that can report `delivered` and has
 * not yet still yields `null`, and `null` is not zero.
 */
export function surfaceReports(surface: Surface, measure: SurfaceMeasure): boolean {
  return SURFACE_CAPABILITY[surface].measures.includes(measure);
}

/** Whether an adapter exists for this surface today. */
export function surfaceIsBuilt(surface: Surface): boolean {
  return SURFACE_BUILT.includes(surface);
}

/**
 * The surfaces one provider carries.
 *
 * One provider, many surfaces — the asymmetry `Channel` alone could not
 * express, and the reason this file exists at all. Meta carries four.
 */
export function surfacesOfProvider(provider: SurfaceProvider): readonly Surface[] {
  return SURFACE_ORDER.filter((surface) => SURFACE_CAPABILITY[surface].provider === provider);
}

/* -------------------------------------------------------------------------- */
/* Where a figure came from → its surface                                      */
/* -------------------------------------------------------------------------- */

/**
 * The surface a campaign is on, from its channel. A campaign is addressed to
 * contacts, so it is always the channel's `direct` surface — never WhatsApp
 * status ads, which are bought and shown rather than sent.
 */
export function surfaceOfChannel(channel: Channel): Surface {
  switch (channel) {
    case 'email':
      return 'email-campaign';
    case 'whatsapp':
      return 'whatsapp-campaign';
    default: {
      const exhaustive: never = channel;
      return exhaustive;
    }
  }
}

/**
 * The surface a Meta placement is, or `null` for `other` — Audience Network,
 * Messenger and whatever Meta adds are real placements with real spend, and
 * not surfaces this product draws. A caller that drops the `null` loses that
 * spend from a per-surface view; the placement is drawn as itself instead.
 * `whatsapp` is ads in WhatsApp Status (slice 46), whose one position is the
 * surface.
 */
export function surfaceOfPlacement(placement: MetaPlacement): Surface | null {
  switch (placement) {
    case 'facebook':
      return 'facebook-ads';
    case 'instagram':
      return 'instagram-ads';
    case 'whatsapp':
      return 'whatsapp-status';
    case 'other':
      return null;
    default: {
      const exhaustive: never = placement;
      return exhaustive;
    }
  }
}

/**
 * The surfaces an ad set's placements promise it. `null` is automatic
 * placements — Meta decides where — so the surfaces promised are Facebook and
 * Instagram ads, never none, and a screen names the rest "Automatic
 * placements". Not WhatsApp Status ads: Meta's placement reference documents
 * Status for an ad set that names it, beside Instagram Stories, and says
 * nothing of automatic placements reaching it — guides say they can. A
 * surface is named for an ad set only when it is certain; whatever Meta then
 * serves is in the figures, read by placement, whatever the promise was.
 */
export function surfacesOfPlacements(
  placements: readonly Exclude<MetaPlacement, 'other'>[] | null,
): readonly Surface[] {
  if (placements === null) return ['facebook-ads', 'instagram-ads'];
  return SURFACE_ORDER.filter((surface) =>
    placements.some((placement) => surfaceOfPlacement(placement) === surface),
  );
}

/**
 * The ad surfaces a draft composed in Wend can name: Facebook, Instagram and,
 * since slice 47, WhatsApp Status — for a draft that opens a WhatsApp chat,
 * with Instagram beside it. A screen that filters Wend's drafts by surface
 * offers these. Automatic placements still promise Facebook and Instagram
 * alone ({@link surfacesOfPlacements}): a draft is promised Status only when it
 * names it, and whatever Meta serves one on automatic placements is in its
 * figures.
 */
export const DRAFTABLE_AD_SURFACES: readonly Surface[] = surfacesOfPlacements(['facebook', 'instagram', 'whatsapp']);

/**
 * A count this surface reported, or `null` where it cannot report one.
 *
 * **`null` carries two different facts and the caller must not collapse them:**
 * the surface cannot report this measure at all, or it can and nothing has
 * come back yet. Both are "no number", and neither is zero. {@link
 * surfaceReports} is how a caller tells them apart, and it is why this shape
 * carries no reason string — a reason would invite a `?? 0` beside it.
 */
export const surfaceCountSchema = z.number().int().min(0).nullable();

export const surfaceFiguresSchema = z
  .object({
    surface: surfaceSchema,
    reached: z.number().int().min(0),
    accepted: surfaceCountSchema,
    delivered: surfaceCountSchema,
    failed: surfaceCountSchema,
    viewed: surfaceCountSchema,
    clicked: surfaceCountSchema,
    impressions: surfaceCountSchema,
    /**
     * Money, in the account's own minor units, with its currency beside it.
     *
     * Never a float: a budget summed in floating point is a budget that drifts,
     * and this is the one figure in the product somebody is billed for. Null on
     * every surface that is not `paid`.
     */
    spendMinor: surfaceCountSchema,
    currency: z.string().length(3).nullable(),
  })
  .strict();
export type SurfaceFigures = z.infer<typeof surfaceFiguresSchema>;

/**
 * Builds a figures row that cannot contradict its own capabilities.
 *
 * Any measure the surface does not report is forced to `null` here, whatever
 * the caller passed. That is the defect class this file was written for: an
 * adapter that reported `delivered: 0` for WhatsApp status would be stating, as
 * a measured fact, that nothing arrived — for a surface where nothing is
 * addressed and arrival is not a thing that happens.
 */
export function surfaceFigures(
  surface: Surface,
  reached: number,
  reported: Partial<Record<SurfaceMeasure, number | null>> = {},
  /**
   * The ad account's currency, and it travels WITH the money or not at all.
   *
   * A spend figure without its currency is a number somebody will compare
   * against another number in a different one. So it is refused below rather
   * than defaulted: there is no sensible default for what somebody was billed
   * in, and guessing is how two ad accounts get summed into a total that
   * belongs to neither.
   */
  currency: string | null = null,
): SurfaceFigures {
  const only = (measure: SurfaceMeasure): number | null =>
    surfaceReports(surface, measure) ? (reported[measure] ?? null) : null;

  const spendMinor = only('spend');
  if (spendMinor !== null && currency === null) {
    throw new Error(
      `surfaceFigures: ${surface} reported spend with no currency. Money without its unit is a figure nobody can compare.`,
    );
  }

  return surfaceFiguresSchema.parse({
    surface,
    reached,
    accepted: only('accepted'),
    delivered: only('delivered'),
    failed: only('failed'),
    viewed: only('viewed'),
    clicked: only('clicked'),
    impressions: only('impressions'),
    spendMinor,
    currency: spendMinor === null ? null : currency,
  });
}

/** Kept for callers that still work in channels — credits, credentials, sends. */
export { channelSchema };
